import styles from './ChartDatePanel.module.css'
import { Box, Button, SxProps} from '@mui/material'
import { DateTime } from 'luxon'
import { useState } from 'react'
import { DateRangePickerDialog } from '../DateRangePickerDialog'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore'
import { LimitedDataForPeriodDialog } from "../LimitedDataForPeriodDialog"
import { DateTimePeriod } from 'shared/types/DateTimePeriod'

export const ChartDateModeList = ['1W', '1M', '3M', '6M', '1Y', 'PREV', 'NEXT', 'Custom'] as const
export type ChartDateMode = typeof ChartDateModeList[number]

export interface ButtonStyle {
    color: {
        default: string,
        hover: string,
        selected: string
    },
    borderColor: {
        default: string,
        hover: string,
        selected: string
    }
}

interface ChartDatePanelProps {
    dateTimePeriod?: DateTimePeriod
    buttonStyle?: ButtonStyle
    mode?: ChartDateMode
    onModeChange?: (mode: ChartDateMode) => void
    onDateTimePeriodChange?: (period: DateTimePeriod) => void
}

export const ChartDatePanel = (props: ChartDatePanelProps): JSX.Element => {
    const {
        dateTimePeriod
    } = props

    const [openDateRangePickerDialog, setOpenDateRangePickerDialog] = useState<boolean>(false)
    const {
        buttonStyle = {
            color: {
                default: '',
                hover: '',
                selected: ''
            },
            borderColor: {
                default: '',
                hover: '',
                selected: ''
            }
        },
        mode = '6M',
        onModeChange,
        onDateTimePeriodChange
    } = props

    const buttonCss: SxProps = {

        '&.MuiButton-root_selected': {
            border: `1px solid ${buttonStyle.borderColor.selected}`,
            color: `${buttonStyle.color.selected}`
        }
    }

    const handleModeClick = (event: ChartDateMode) => {
        if (onModeChange) {
            onModeChange(event)
        }

        if (onDateTimePeriodChange) {
            switch (event) {
                case '1W': {
                    const from = DateTime.now().startOf('day')
                    const to = DateTime.now().endOf('day').plus({ days: 7 })

                    const period: DateTimePeriod = {
                        from: from,
                        to: to
                    }

                    onDateTimePeriodChange(period)

                    break;
                }
                case '1M': {
                    const from = DateTime.now().startOf('day')
                    const to = DateTime.now().endOf('day').plus({ month: 1 })

                    const period: DateTimePeriod = {
                        from: from,
                        to: to
                    }

                    onDateTimePeriodChange(period)

                    break;
                }
                case '3M': {
                    const from = DateTime.now().startOf('day')
                    const to = DateTime.now().endOf('day').plus({ month: 3 })

                    const period: DateTimePeriod = {
                        from: from,
                        to: to
                    }

                    onDateTimePeriodChange(period)

                    break;
                }
                case '6M': {
                    const from = DateTime.now().startOf('day')
                    const to = DateTime.now().endOf('day').plus({ month: 6 })

                    const period: DateTimePeriod = {
                        from: from,
                        to: to
                    }

                    onDateTimePeriodChange(period)

                    break;
                }
                case '1Y': {
                    const from = DateTime.now().startOf('day')
                    const to = DateTime.now().endOf('day').plus({ month: 12 })

                    const period: DateTimePeriod = {
                        from: from,
                        to: to
                    }

                    onDateTimePeriodChange(period)

                    break;
                }

                case 'PREV': {
                    if (dateTimePeriod === undefined) {
                        return
                    }

                    const from = dateTimePeriod?.from.minus({ month: 1 })
                    const to = dateTimePeriod?.to.minus({ month: 1 })

                    const period: DateTimePeriod = {
                        from: from,
                        to: to
                    }

                    onDateTimePeriodChange(period)

                    break;
                }

                case 'NEXT': {
                    if (dateTimePeriod === undefined) {
                        return
                    }

                    const from = dateTimePeriod?.from.plus({ month: 1 })
                    const to = dateTimePeriod?.to.plus({ month: 1 })

                    const period: DateTimePeriod = {
                        from: from,
                        to: to
                    }

                    onDateTimePeriodChange(period)

                    break;
                }

                case 'Custom': {
                    setOpenDateRangePickerDialog(true)
                    break
                }

                default: {

                }
            }
        }
    }

    const renderList = () => {
        return ChartDateModeList.map((chartDateModeItem) => {
            if (chartDateModeItem === 'PREV') {
                return (
                    <Button
                        onClick={() => handleModeClick(chartDateModeItem)}
                        sx={buttonCss}
                        key={chartDateModeItem}
                        variant="outlined"
                        className={chartDateModeItem === mode ? 'MuiButton-root_selected' : ''}
                    >
                        <NavigateBeforeIcon></NavigateBeforeIcon>
                    </Button>
                )
            }

            if (chartDateModeItem === 'NEXT') {
                return (
                    <Button
                        onClick={() => handleModeClick(chartDateModeItem)}
                        sx={buttonCss}
                        key={chartDateModeItem}
                        variant="outlined"
                        className={chartDateModeItem === mode ? 'MuiButton-root_selected' : ''}
                    >
                        <NavigateNextIcon></NavigateNextIcon>
                    </Button>
                )
            }

            return (
                <Button
                    onClick={() => handleModeClick(chartDateModeItem)}
                    sx={buttonCss}
                    key={chartDateModeItem}
                    variant="outlined"
                    className={chartDateModeItem === mode ? 'MuiButton-root_selected': ''}
                >
                    {chartDateModeItem}
                </Button>
            )
        })
    }

    const handleDateRangePickerDialogClose = () => {
        setOpenDateRangePickerDialog(false)
    }

    const handleDateRangePickerDialogOk = (period: any) => {
        if (onDateTimePeriodChange) {
            const per: DateTimePeriod = {
                from: period.startDate,
                to: period.endDate
            }

            onDateTimePeriodChange(per)
        }

        setOpenDateRangePickerDialog(false)
    }

    return (
        <>
            <div
                className={styles.ChartDatePanel__ActionsContainer}
            >
                {
                    renderList()
                }
            </div>

            <DateRangePickerDialog
                open={openDateRangePickerDialog}
                initialStartDate={dateTimePeriod?.from}
                initialEndDate={dateTimePeriod?.to}
                onClose={handleDateRangePickerDialogClose}
                onOk={handleDateRangePickerDialogOk}
                fullScreen
            />       
        </>
    )
}